import React, { useEffect, useRef, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const clock = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "";

export default function Chat() {
  const { user } = useAuth();
  const [access, setAccess] = useState(undefined);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const endRef = useRef(null);
  const activeRef = useRef(null);
  const lastIdRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => { api.get("/chat/me/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ enabled: false })); }, []);

  const loadConvs = useCallback(() => {
    api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => {});
  }, []);
  useEffect(() => { if (access?.enabled) loadConvs(); }, [access, loadConvs]);

  // Always-on conversation-list polling while on the Chat page — keeps the list,
  // latest message, and unread badges live without navigating away and back.
  useEffect(() => {
    if (!access?.enabled) return;
    const iv = setInterval(loadConvs, 3000);
    return () => clearInterval(iv);
  }, [access, loadConvs]);

  const openConv = useCallback(async (c) => {
    setActive(c); activeRef.current = c.id;
    const { data } = await api.get(`/chat/conversations/${c.id}/messages?limit=30`);
    setMessages(data); lastIdRef.current = data.length ? data[data.length - 1].created_at : null;
    await api.post(`/chat/conversations/${c.id}/read`).catch(() => {});
    loadConvs();
    setTimeout(() => endRef.current?.scrollIntoView(), 50);
  }, [loadConvs]);

  // Live message polling for the open conversation (near real-time via short-polling).
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(async () => {
      if (!activeRef.current || !lastIdRef.current) return;
      try {
        const { data } = await api.get(`/chat/conversations/${activeRef.current}/messages?after=${encodeURIComponent(lastIdRef.current)}&limit=50`);
        if (data.length) {
          setMessages((m) => [...m, ...data]);
          lastIdRef.current = data[data.length - 1].created_at;
          await api.post(`/chat/conversations/${activeRef.current}/read`).catch(() => {});
          setTimeout(() => endRef.current?.scrollIntoView(), 30);
        }
      } catch { /* network blip — auto-retries next tick */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [active]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !active) return;
    setDraft("");
    try {
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, { body });
      setMessages((m) => [...m, data]); lastIdRef.current = data.created_at;
      loadConvs();
      setTimeout(() => endRef.current?.scrollIntoView(), 30);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); setDraft(body); }
  };

  const doSearch = (val) => {
    setQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      api.get(`/chat/contacts?q=${encodeURIComponent(val)}`).then(({ data }) => setResults(data)).catch(() => setResults([]));
    }, 350);
  };
  useEffect(() => { if (newOpen) doSearch(""); }, [newOpen]);

  const startWith = async (c) => {
    try {
      const { data } = await api.post("/chat/conversations", { recipient_user_id: c.user_id });
      setNewOpen(false); setQ(""); setResults([]);
      await loadConvs();
      openConv({ id: data.id, other_name: c.full_name, other_department: c.department, other_user_id: c.user_id });
    } catch (e) { toast.error(e.response?.data?.detail || "Can't start conversation"); }
  };

  if (access === undefined) return <div className="p-8 text-slate-400">Loading…</div>;
  if (!access.enabled) return (
    <div className="uemp-fade-in">
      <PageHeader title="Chat" subtitle="Internal employee messaging." />
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="chat-no-access">
        <Icons.MessageSquareOff className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        Chat is not available for your account.
      </div>
    </div>
  );

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Chat" subtitle="Fast, private 1:1 messaging with your colleagues."
        actions={access.can_start && <Button onClick={() => setNewOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="chat-new-btn"><Icons.Plus className="h-4 w-4 mr-1.5" /> New Chat</Button>} />

      <div className="grid md:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[460px]">
        {/* Conversation list */}
        <div className={`${active ? "hidden md:block" : "block"} bg-white rounded-xl border border-slate-200 overflow-y-auto`} data-testid="chat-conv-list">
          {convs.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No conversations yet.</div>}
          {convs.map((c) => (
            <button key={c.id} onClick={() => openConv(c)} data-testid={`chat-conv-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${active?.id === c.id ? "bg-slate-50" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm truncate">{c.other_name}</span>
                <span className="text-[11px] text-slate-400 shrink-0 ml-2">{relTime(c.last_message_at)}</span>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-xs text-slate-500 truncate">{c.last_sender_id === user.id ? "You: " : ""}{c.last_message || "—"}</span>
                {c.unread > 0 && <Badge className="bg-blue-600 text-white hover:opacity-100 h-5 min-w-5 px-1.5 text-[10px] shrink-0 ml-2" data-testid={`chat-unread-${c.id}`}>{c.unread}</Badge>}
              </div>
            </button>
          ))}
        </div>

        {/* Message panel */}
        <div className={`${active ? "flex" : "hidden md:flex"} md:col-span-2 bg-white rounded-xl border border-slate-200 flex-col`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation to start messaging.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <button onClick={() => { setActive(null); activeRef.current = null; }} className="md:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="chat-back-btn">
                  <Icons.ChevronLeft className="h-5 w-5" />
                </button>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">{active.other_name}</div>
                  {active.other_department && <div className="text-[11px] text-slate-400 truncate">{active.other_department}</div>}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.mine ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${m.mine ? "text-slate-300 justify-end" : "text-slate-400"}`}>
                        {clock(m.created_at)}
                        {m.mine && (m.read ? <Icons.CheckCheck className="h-3 w-3" /> : <Icons.Check className="h-3 w-3" />)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={access.max_length}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message…" data-testid="chat-input" />
                <Button onClick={send} disabled={!draft.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="chat-send-btn"><Icons.Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Conversation</DialogTitle><DialogDescription>Search colleagues you're allowed to contact.</DialogDescription></DialogHeader>
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Search by name…" className="pl-9" data-testid="chat-contact-search" />
          </div>
          <div className="max-h-72 overflow-y-auto mt-2 divide-y" data-testid="chat-contact-results">
            {results.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No eligible contacts found.</div>}
            {results.map((c) => (
              <button key={c.user_id} onClick={() => startWith(c)} className="w-full text-left px-2 py-2.5 hover:bg-slate-50 rounded-lg flex items-center justify-between" data-testid={`chat-contact-${c.user_id}`}>
                <span className="text-sm text-slate-800">{c.full_name}</span>
                {c.department && <span className="text-[11px] text-slate-400">{c.department}</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
